/**
 * Google Analytics 4 Utility Functions
 * 
 * Centralized analytics tracking for DSI Durango website.
 * Tracks user interactions, conversions, and engagement.
 * 
 * GA4 Property ID: G-295106505
 * 
 * @module analytics
 */

/**
 * Check if Google Analytics is loaded
 * @returns {boolean}
 */
const isGALoaded = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track page view
 * Called automatically by GA4, but can be used for SPAs
 * 
 * @param {string} page_title - Page title
 * @param {string} page_path - Page path (e.g., '/cnc')
 */
export const trackPageView = (page_title, page_path) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'page_view', {
    page_title,
    page_path,
    page_location: window.location.href
  });
};

/**
 * Track language change
 * 
 * @param {string} language - 'en' or 'es'
 */
export const trackLanguageChange = (language) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'language_change', {
    event_category: 'engagement',
    event_label: language,
    language: language
  });
};

/**
 * Track certification badge click
 * 
 * @param {string} badge_type - 'iso' or 'dynalab'
 * @param {string} page - Current page name
 */
export const trackBadgeClick = (badge_type, page) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'badge_click', {
    event_category: 'engagement',
    event_label: `${badge_type}_${page}`,
    badge_type,
    page_location: page
  });
};

/**
 * Track CTA button click
 * 
 * @param {string} cta_type - 'get_quote', 'whatsapp', 'email', 'phone'
 * @param {string} page - Current page name
 * @param {string} location - Button location (e.g., 'hero', 'mid_page', 'footer')
 */
export const trackCTAClick = (cta_type, page, location = 'unknown') => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'cta_click', {
    event_category: 'conversion',
    event_label: `${cta_type}_${page}_${location}`,
    cta_type,
    page,
    button_location: location
  });
};

/**
 * Track contact form submission
 * 
 * @param {string} form_type - 'bitrix24', 'contact', 'quote'
 * @param {string} page - Current page name
 */
export const trackFormSubmission = (form_type, page) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'form_submission', {
    event_category: 'conversion',
    event_label: `${form_type}_${page}`,
    form_type,
    page_location: page
  });
};

/**
 * Track gallery image view
 * 
 * @param {string} page - Current page name ('cnc', 'harness', 'automation')
 * @param {number} image_index - Image index (0-based)
 */
export const trackGalleryView = (page, image_index) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'gallery_view', {
    event_category: 'engagement',
    event_label: `${page}_image_${image_index}`,
    page,
    image_index
  });
};

/**
 * Track service card click
 * 
 * @param {string} service - 'cnc', 'harness', 'automation'
 */
export const trackServiceClick = (service) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'service_click', {
    event_category: 'navigation',
    event_label: service,
    service_type: service
  });
};

/**
 * Track WhatsApp button click
 * 
 * @param {string} page - Current page name
 * @param {string} phone_number - WhatsApp number
 */
export const trackWhatsAppClick = (page, phone_number) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'whatsapp_click', {
    event_category: 'conversion',
    event_label: `whatsapp_${page}`,
    page,
    phone_number
  });
};

/**
 * Track email click
 * 
 * @param {string} email - Email address clicked
 * @param {string} page - Current page name
 */
export const trackEmailClick = (email, page) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'email_click', {
    event_category: 'conversion',
    event_label: `email_${page}`,
    email_type: email.includes('sales') ? 'sales' : 'ventas',
    page
  });
};

/**
 * Track phone click
 * 
 * @param {string} phone - Phone number
 * @param {string} location_name - Office location (e.g., 'Durango HQ')
 * @param {string} page - Current page name
 */
export const trackPhoneClick = (phone, location_name, page) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'phone_click', {
    event_category: 'conversion',
    event_label: `phone_${location_name}_${page}`,
    phone_number: phone,
    office_location: location_name,
    page
  });
};

/**
 * Track PDF download
 * 
 * @param {string} pdf_name - PDF filename (e.g., 'ISO-9001-Certificate')
 * @param {string} page - Current page name
 */
export const trackPDFDownload = (pdf_name, page) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'file_download', {
    event_category: 'engagement',
    event_label: pdf_name,
    file_name: pdf_name,
    file_extension: 'pdf',
    page
  });
};

/**
 * Track navigation menu click
 * 
 * @param {string} destination - Destination page (e.g., 'cnc', 'automation')
 * @param {string} source_page - Current page
 */
export const trackNavigation = (destination, source_page) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'navigation_click', {
    event_category: 'navigation',
    event_label: `${source_page}_to_${destination}`,
    destination,
    source_page
  });
};

/**
 * Track scroll depth
 * 
 * @param {number} percentage - Scroll percentage (25, 50, 75, 100)
 * @param {string} page - Current page name
 */
export const trackScrollDepth = (percentage, page) => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'scroll', {
    event_category: 'engagement',
    event_label: `${percentage}%_${page}`,
    scroll_depth: percentage,
    page
  });
};

/**
 * Track outbound link click
 * 
 * @param {string} url - External URL
 * @param {string} link_text - Link text or description
 */
export const trackOutboundLink = (url, link_text = '') => {
  if (!isGALoaded()) return;
  
  window.gtag('event', 'click', {
    event_category: 'outbound',
    event_label: url,
    link_url: url,
    link_text
  });
};

/**
 * Set user properties
 * 
 * @param {Object} properties - User properties object
 */
export const setUserProperties = (properties) => {
  if (!isGALoaded()) return;
  
  window.gtag('set', 'user_properties', properties);
};

export default {
  trackPageView,
  trackLanguageChange,
  trackBadgeClick,
  trackCTAClick,
  trackFormSubmission,
  trackGalleryView,
  trackServiceClick,
  trackWhatsAppClick,
  trackEmailClick,
  trackPhoneClick,
  trackPDFDownload,
  trackNavigation,
  trackScrollDepth,
  trackOutboundLink,
  setUserProperties
};
